/**
 * Geometry for linear and area features (rivers, national forests, wilderness). Wikipedia
 * geosearch pins a river to its mouth, so the North Santiam was invisible for the seventy minutes
 * OR-22 runs beside it (H27). The Worker indexes the real lines and outlines by cell, clips them
 * to the corridor it answers for, and the phone decides from them whether the car is beside a
 * river or inside a forest.
 *
 * Coordinates are [lat, lon] pairs. A river is a list of polyline parts; a forest is a list of
 * rings tested even-odd, so holes (private land inside a national forest) need no winding rules.
 */
import type { BBox } from './geo';

export type LatLon = [number, number];
/** A region is an EPA Level III ecoregion (section 9 of the Santiam plan): an outline like a forest. */
export type FeatureKind = 'river' | 'forest' | 'wilderness' | 'region';

/** Rivers are lines; forests and wilderness are outlines. */
export const isLineKind = (k: FeatureKind): boolean => k === 'river';

/**
 * Index cells are coarse on purpose: membership is decided from the geometry, not the cell, so
 * small cells would only multiply lookups (Salem to Santiam Junction is about 8 at 0.25 degrees).
 */
export const FEATURE_CELL_DEG = 0.25;

/** A lookup also sees this far past its cell, so a river just over the edge from a road in the cell is found. */
export const FEATURE_CELL_MARGIN_KM = 2;

export interface Cell extends BBox {
  key: string;
}

export function featureCell(lat: number, lon: number, deg = FEATURE_CELL_DEG): Cell {
  const i = Math.floor(lat / deg);
  const j = Math.floor(lon / deg);
  const minLat = i * deg;
  const minLon = j * deg;
  return { key: `${minLat.toFixed(2)},${minLon.toFixed(2)}`, minLat, minLon, maxLat: minLat + deg, maxLon: minLon + deg };
}

/** Every cell a box touches. */
export function cellsInBox(box: BBox, deg = FEATURE_CELL_DEG): Cell[] {
  const out: Cell[] = [];
  for (let i = Math.floor(box.minLat / deg); i * deg < box.maxLat; i++) {
    for (let j = Math.floor(box.minLon / deg); j * deg < box.maxLon; j++) out.push(featureCell((i + 0.5) * deg, (j + 0.5) * deg, deg));
  }
  return out;
}

/** A box grown by `km` on every side. */
export function expandBox(c: BBox, km: number): BBox {
  const dLat = km / 111;
  const dLon = km / (111 * Math.cos((((c.minLat + c.maxLat) / 2) * Math.PI) / 180));
  return { minLat: c.minLat - dLat, maxLat: c.maxLat + dLat, minLon: c.minLon - dLon, maxLon: c.maxLon + dLon };
}

export function geomBox(geom: LatLon[][]): BBox {
  const b = { minLat: Infinity, maxLat: -Infinity, minLon: Infinity, maxLon: -Infinity };
  for (const part of geom) {
    for (const [la, lo] of part) {
      if (la < b.minLat) b.minLat = la;
      if (la > b.maxLat) b.maxLat = la;
      if (lo < b.minLon) b.minLon = lo;
      if (lo > b.maxLon) b.maxLon = lo;
    }
  }
  return b;
}

export const boxesOverlap = (a: BBox, b: BBox): boolean =>
  a.minLat <= b.maxLat && a.maxLat >= b.minLat && a.minLon <= b.maxLon && a.maxLon >= b.minLon;

const segBox = (a: LatLon, b: LatLon): BBox => ({
  minLat: Math.min(a[0], b[0]),
  maxLat: Math.max(a[0], b[0]),
  minLon: Math.min(a[1], b[1]),
  maxLon: Math.max(a[1], b[1]),
});

/**
 * The runs of each polyline whose segments reach into the box, both ends of each segment kept,
 * so a line leaving the box still shows which way it went. Conservative: a segment whose own
 * bounding box touches the box counts.
 */
export function clipLines(parts: LatLon[][], box: BBox): LatLon[][] {
  const out: LatLon[][] = [];
  for (const part of parts) {
    let run: LatLon[] = [];
    for (let i = 1; i < part.length; i++) {
      const a = part[i - 1]!;
      const b = part[i]!;
      if (boxesOverlap(segBox(a, b), box)) {
        if (!run.length) run.push(a);
        run.push(b);
      } else if (run.length) {
        out.push(run);
        run = [];
      }
    }
    if (run.length) out.push(run);
  }
  return out;
}

/**
 * Sutherland-Hodgman against the box, ring by ring. Clipping can leave edges running along the
 * box's border, which an even-odd test inside the box does not mind.
 */
export function clipRings(rings: LatLon[][], box: BBox): LatLon[][] {
  const edges: [(p: LatLon) => boolean, (a: LatLon, b: LatLon) => LatLon][] = [
    [(p) => p[0] >= box.minLat, (a, b) => cut(a, b, 0, box.minLat)],
    [(p) => p[0] <= box.maxLat, (a, b) => cut(a, b, 0, box.maxLat)],
    [(p) => p[1] >= box.minLon, (a, b) => cut(a, b, 1, box.minLon)],
    [(p) => p[1] <= box.maxLon, (a, b) => cut(a, b, 1, box.maxLon)],
  ];
  const out: LatLon[][] = [];
  for (const ring of rings) {
    if (!boxesOverlap(geomBox([ring]), box)) continue;
    let poly = ring;
    for (const [inside, at] of edges) {
      const next: LatLon[] = [];
      for (let i = 0; i < poly.length; i++) {
        const cur = poly[i]!;
        const prev = poly[(i + poly.length - 1) % poly.length]!;
        if (inside(cur)) {
          if (!inside(prev)) next.push(at(prev, cur));
          next.push(cur);
        } else if (inside(prev)) next.push(at(prev, cur));
      }
      poly = next;
      if (!poly.length) break;
    }
    if (poly.length >= 3) out.push(poly);
  }
  return out;
}

function cut(a: LatLon, b: LatLon, axis: 0 | 1, v: number): LatLon {
  const t = (v - a[axis]) / (b[axis] - a[axis]);
  const p: LatLon = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  p[axis] = v;
  return p;
}

/** Even-odd point in polygon; holes are just more rings. */
export function insideRings(lat: number, lon: number, rings: LatLon[][]): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [yi, xi] = ring[i]!;
      const [yj, xj] = ring[j]!;
      if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
}

const M_PER_DEG = 111_320;

export interface LineNearest {
  /** Metres from the point to the nearest segment. */
  distM: number;
  /** Direction of that segment as drawn, degrees [0, 360). Rivers are matched either way along. */
  bearing: number;
}

/**
 * Nearest segment of any part, in a flat projection round the point: fine for the few
 * kilometres that matter, and cheap enough for every fix.
 */
export function nearestOnLines(lat: number, lon: number, parts: LatLon[][]): LineNearest | null {
  const kx = M_PER_DEG * Math.cos((lat * Math.PI) / 180);
  let best: LineNearest | null = null;
  for (const part of parts) {
    for (let i = 1; i < part.length; i++) {
      const ax = (part[i - 1]![1] - lon) * kx;
      const ay = (part[i - 1]![0] - lat) * M_PER_DEG;
      const bx = (part[i]![1] - lon) * kx;
      const by = (part[i]![0] - lat) * M_PER_DEG;
      const dx = bx - ax;
      const dy = by - ay;
      const len2 = dx * dx + dy * dy;
      const t = len2 > 0 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0;
      const px = ax + dx * t;
      const py = ay + dy * t;
      const d = Math.hypot(px, py);
      if (!best || d < best.distM) best = { distM: d, bearing: ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360 };
    }
  }
  return best;
}

/**
 * Distance from a point to a feature: to the line for a river, 0 inside an outline and the
 * distance to its edge outside it.
 */
export function distanceToFeature(lat: number, lon: number, kind: FeatureKind, geom: LatLon[][]): number {
  if (!isLineKind(kind) && insideRings(lat, lon, geom)) return 0;
  const edges = isLineKind(kind) ? geom : geom.map((r) => (r.length ? [...r, r[0]!] : r));
  return nearestOnLines(lat, lon, edges)?.distM ?? Infinity;
}

/** Round to 4 decimals (about 11 m), which keeps the corridor response small. */
export const roundGeom = (geom: LatLon[][]): LatLon[][] =>
  geom.map((p) => p.map(([la, lo]) => [Math.round(la * 1e4) / 1e4, Math.round(lo * 1e4) / 1e4] as LatLon));
